import React, { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import Draggable from "react-draggable";
import {
  Minus,
  Maximize2,
  Minimize2,
  X,
  Clock,
  LayoutList,
  LayoutGrid,
  Square,
  SquareStack,
} from "lucide-react";
import { Tooltip } from "antd";
import {
  hideFloatingConsultation,
  hideJoinButton,
} from "../../actions/RPMAction";
import ConformModal from "../Modals/Reusable Modals/ConformModal";
import {
  DoctorConsultation,
  PatientConsultation,
} from "../../pages/zoom-video-consultation/ConsultationLanding";
import { useParams, Link } from "react-router-dom";
import styles from "./FloatingConsultation.module.scss";

const INITIAL_WIDTH = 350;

const FloatingConsultation = () => {
  const dispatch = useDispatch();
  const rpmreducer = useSelector((state) => state.rpmreducer) || {};
  const { floatingConsultation = {}, consultationdetails } = rpmreducer;
  const { visible, appointmentId, role } = floatingConsultation;
  const { patientId: patientIdFromParams } = useParams();

  // Load patient details from localStorage as requested
  const storedDetails = JSON.parse(
    localStorage.getItem("floatingdetails") || "{}"
  );
  console.log("Loaded storedDetails:", storedDetails);
  const patientName =
    consultationdetails?.patientName ||
    storedDetails?.patientName ||
    localStorage.getItem("PatientName") ||
    "Patient";

  const patientId =
    consultationdetails?.patientId ||
    storedDetails?.patientId ||
    patientIdFromParams;

  const [mode, setMode] = useState("normal"); // normal | minimized | maximized
  const [viewMode, setViewMode] = useState("single"); // single | multi
  const [floatingContentHeight, setFloatingContentHeight] = useState(0); // px — driven by video slot count
  const [elapsed, setElapsed] = useState(0);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    let interval;
    if (visible) {
      interval = setInterval(() => {
        setElapsed((prev) => prev + 1);
      }, 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(interval);
  }, [visible]);

  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleClose = () => {
    setShowCloseModal(true);
  };

  const handleConfirmClose = () => {
    setShowCloseModal(false);
    dispatch(hideFloatingConsultation());
    dispatch(hideJoinButton());
    localStorage.removeItem("floatingdetails");
  };

  const toggleMaximize = () => {
    setMode(mode === "maximized" ? "normal" : "maximized");
  };

  const toggleMinimize = () => {
    setMode(mode === "minimized" ? "normal" : "minimized");
  };

  // Callback from VideoConsultation to notify how many video slots are showing
  const handleFloatingHeightChange = useCallback((heightPx) => {
    setFloatingContentHeight(heightPx);
  }, []);

  if (!visible) return null;

  const isMin = mode === "minimized";
  const isMax = mode === "maximized";
  const isNormal = mode === "normal";

  const HEADER_H = 44; // px — matches minHeight on the header bar

  const getStyle = () => {
    if (isMax) {
      return {
        position: "fixed",
        top: "0",
        left: "0",
        right: "0",
        bottom: "0",
        width: "100%",
        height: "100%",
        zIndex: 9999,
      };
    }
    if (isMin) {
      return {
        width: `${INITIAL_WIDTH}px`,
        height: "44px",
      };
    }
    // Normal mode: explicit pixel height so the window doesn't collapse.
    // floatingContentHeight = (slotCount * SLOT_HEIGHT) + CONTROL_BAR_HEIGHT
    // We add HEADER_H for the drag bar on top.
    return {
      width: `${INITIAL_WIDTH}px`,
      height: `${floatingContentHeight + HEADER_H}px`,
      maxHeight: "90vh",
    };
  };

  const content = (
    <div
      ref={containerRef}
      style={getStyle()}
      className={`flex flex-col rounded-2xl overflow-hidden shadow-2xl border border-slate-200 bg-black ${isMax ? styles.maximized : ""} ${isMin ? styles.minimized : ""}`}
    >
      {/* ─── Header / Drag Bar ─────────────────────────────────────── */}
      <div
        className="handle flex items-center justify-between px-3 bg-gradient-to-r from-blue-800 to-blue-700 select-none cursor-grab active:cursor-grabbing shrink-0"
        style={{ minHeight: `${HEADER_H}px` }}
      >
        {/* Left — title + timer */}
        <div className="flex items-center gap-2.5">
          <div className="flex items-center gap-1.5 min-w-0">
            {patientName ? (
              <Tooltip
                title={`Go to ${patientName}'s details`}
                placement="top"
                color="#3b82f6"
                overlayInnerStyle={{
                  color: "#fff",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                <Link
                  to={`/bridge/patient-details/${patientId}`}
                  className="text-white text-sm font-semibold leading-tight hover:underline truncate max-w-[150px] block" // removed pb-[1px]
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {patientName}
                </Link>
              </Tooltip>
            ) : (
              <span className="text-white text-sm font-semibold leading-tight">
                Consultation
              </span>
            )}
            <div className="flex items-center gap-1 text-[11px] text-white/70 shrink-0 leading-none">
              <span>(</span>
              <Clock size={11} className="relative -top-[0.5px]" />
              <span>{formatTime(elapsed)}</span>
              <span>)</span>
            </div>
          </div>
        </div>

        {/* Right — controls */}
        <div
          className="flex items-center gap-1"
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Tooltip
            title={isMin ? "Expand" : "Minimize"}
            color="#3b82f6"
            overlayInnerStyle={{
              color: "#fff",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            <button
              onClick={toggleMinimize}
              className="h-6 w-6 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors"
            >
              <Minus size={14} />
            </button>
          </Tooltip>
          {/* Single / Multi toggle — in normal or minimized mode */}
          {(isNormal || isMin) && (
            <>
              <Tooltip
                title="Single View"
                color="#3b82f6"
                overlayInnerStyle={{
                  color: "#fff",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                <button
                  onClick={() => {
                    setViewMode("single");
                    if (isMin) setMode("normal");
                  }}
                  className={`h-6 w-6 rounded-md flex items-center justify-center transition-colors ${
                    viewMode === "single"
                      ? "bg-white/30 text-white"
                      : "text-white/60 hover:text-white hover:bg-white/15"
                  }`}
                >
                  <Square size={13} />
                </button>
              </Tooltip>
              <Tooltip
                title="Multi View"
                color="#3b82f6"
                overlayInnerStyle={{
                  color: "#fff",
                  borderRadius: "8px",
                  fontSize: "12px",
                  fontWeight: "500",
                }}
              >
                <button
                  onClick={() => {
                    setViewMode("multi");
                    if (isMin) setMode("normal");
                  }}
                  className={`h-6 w-6 rounded-md flex items-center justify-center transition-colors ${
                    viewMode === "multi"
                      ? "bg-white/30 text-white"
                      : "text-white/60 hover:text-white hover:bg-white/15"
                  }`}
                >
                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 13 13"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect
                      x="1"
                      y="1"
                      width="11"
                      height="4.5"
                      rx="1"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      fill="none"
                    />
                    <rect
                      x="1"
                      y="7.5"
                      width="11"
                      height="4.5"
                      rx="1"
                      stroke="currentColor"
                      strokeWidth="1.2"
                      fill="none"
                    />
                  </svg>
                </button>
              </Tooltip>
            </>
          )}
          <Tooltip
            title={isMax ? "Restore" : "Maximize"}
            color="#3b82f6"
            overlayInnerStyle={{
              color: "#fff",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            <button
              onClick={toggleMaximize}
              className="h-6 w-6 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors"
            >
              {isMax ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </Tooltip>
          {/* Close — only in minimized or maximized mode */}
          {/* {(isMin || isMax) && (
            <Tooltip
              title="Close"
              color="#3b82f6"
              overlayInnerStyle={{
                color: "#fff",
                borderRadius: "8px",
                fontSize: "12px",
                fontWeight: "500",
              }}
            >
              <button
                onClick={handleClose}
                className="h-6 w-6 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/15 transition-colors"
              >
                <X size={14} />
              </button>
            </Tooltip>
          )} */}
        </div>
      </div>

      {/* ─── Body ──────────────────────────────────────────────── */}
      <div
        className={isMax ? styles.maximizedBody : ""}
        style={{
          display: isMin ? "none" : "flex",
          flex: 1,
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          backgroundColor: "#0f172a",
          minHeight: 0,
        }}
      >
        {role === "Web_Doctor" ? (
          <DoctorConsultation
            appointmentId={appointmentId}
            onLeave={() => {
              dispatch(hideFloatingConsultation());
              dispatch(hideJoinButton());
              localStorage.removeItem("floatingdetails");
            }}
            isFloating={true}
            floatingMode={mode}
            floatingViewMode={viewMode}
            onFloatingHeightChange={
              isNormal ? handleFloatingHeightChange : undefined
            }
          />
        ) : (
          <PatientConsultation
            appointmentId={appointmentId}
            onLeave={() => {
              dispatch(hideFloatingConsultation());
              dispatch(hideJoinButton());
              localStorage.removeItem("floatingdetails");
            }}
            isFloating={true}
            floatingMode={mode}
            floatingViewMode={viewMode}
            onFloatingHeightChange={
              isNormal ? handleFloatingHeightChange : undefined
            }
          />
        )}
      </div>
    </div>
  );

  return (
    <>
      <Draggable handle=".handle" bounds="body" disabled={isMax}>
        <div
          className={
            isMax
              ? `fixed inset-0 z-[9999] ${styles.maximizedWrapper}`
              : "fixed bottom-6 right-6 z-[9999]"
          }
        >
          {content}
        </div>
      </Draggable>
      <ConformModal
        isShowModal={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onOk={handleConfirmClose}
        title="Close Consultation?"
        okText="Yes"
        closeText="Cancel"
      />
    </>
  );
};

export default FloatingConsultation;
